import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ArrowLeft, Clock, Wrench, DollarSign, TrendingUp, CheckCircle, XCircle, AlertTriangle, FileText, Activity } from 'lucide-react';

const EquipmentTimelinePage = () => {
  const { equipmentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [costAnalysis, setCostAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (equipmentId) {
      fetchTimeline();
      fetchCostAnalysis();
    }
  }, [equipmentId]);

  const fetchTimeline = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/analytics/equipment/${equipmentId}/timeline`);
      setData(response.data);
    } catch (error) {
      toast.error('Eroare la incarcare');
    } finally {
      setLoading(false);
    }
  };

  const fetchCostAnalysis = async () => {
    try {
      const response = await api.get(`/analytics/equipment/${equipmentId}/cost-analysis`);
      setCostAnalysis(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(value || 0);
  };

  const getEventIcon = (type, status) => {
    if (type === 'ticket') {
      if (status === 'resolved') return <CheckCircle className="w-5 h-5 text-green-600" />;
      if (status === 'in_progress') return <Clock className="w-5 h-5 text-yellow-600" />;
      return <AlertTriangle className="w-5 h-5 text-red-600" />;
    }
    return <Wrench className="w-5 h-5 text-blue-600" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!data) {
    return <div className="text-center py-12"><p className="text-slate-500">Echipament negasit</p></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/settings/equipment')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Inapoi
          </Button>
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">{data.equipment?.name}</h1>
            <p className="text-slate-600">Cod: {data.equipment?.code} | Istoric complet</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Activity className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Evenimente</p>
                <p className="text-3xl lg:text-4xl font-bold">{data.total_events}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Incidente</p>
                <p className="text-3xl lg:text-4xl font-bold">{data.total_tickets}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Wrench className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Revizii</p>
                <p className="text-3xl lg:text-4xl font-bold">{data.total_revisions}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Cost Total (TCO)</p>
                <p className="text-lg font-bold">{formatCurrency(costAnalysis?.tco || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Uptime</p>
                <p className="text-3xl lg:text-4xl font-bold">{data.uptime_days} zile</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {costAnalysis && (
        <Card>
          <CardHeader>
            <CardTitle>Analiza Costuri - 12 Luni</CardTitle>
            <CardDescription>Reparatii vs Revizii preventive</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={costAnalysis.monthly_breakdown}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" style={{ fontSize: 12 }} />
                <YAxis style={{ fontSize: 12 }} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="repair_cost" name="Reparatii" fill="#EF4444" />
                <Bar dataKey="revision_cost" name="Revizii" fill="#10B981" />
              </BarChart>
            </ResponsiveContainer>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t">
              <div className="text-center">
                <p className="text-sm text-slate-500">Cost Mentenanta</p>
                <p className="text-lg font-bold text-purple-600">{formatCurrency(costAnalysis.total_maintenance_cost)}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-slate-500">Pret Achizitie</p>
                <p className="text-lg font-bold text-blue-600">{formatCurrency(costAnalysis.purchase_price)}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-slate-500">Cost/Zi</p>
                <p className="text-lg font-bold text-orange-600">{formatCurrency(costAnalysis.cost_per_day)}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-slate-500">Mentenanta/Achizitie</p>
                <p className="text-lg font-bold text-red-600">{costAnalysis.maintenance_vs_purchase_ratio}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Istoric Cronologic</CardTitle>
          <CardDescription>{data.total_events} evenimente inregistrate</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {data.timeline?.map((event, idx) => (
              <div key={idx} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                    {getEventIcon(event.type, event.status)}
                  </div>
                  {idx < data.timeline.length - 1 && (
                    <div className="w-0.5 h-full bg-slate-200 my-2"></div>
                  )}
                </div>
                <div className="flex-1 pb-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-slate-900">{event.title}</p>
                        <Badge variant="outline" className="text-xs">
                          {event.type === 'ticket' ? 'Tichet' : 'Revizie'}
                        </Badge>
                        {event.status && (
                          <Badge className={`text-xs ${
                            event.status === 'completed' || event.status === 'resolved' ? 'bg-green-100 text-green-700' :
                            event.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {event.status}
                          </Badge>
                        )}
                      </div>
                      {event.description && (
                        <p className="text-sm text-slate-600 line-clamp-2">{event.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(event.date).toLocaleString('ro-RO')}
                        </span>
                        {event.cost > 0 && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-3 h-3" />
                            {formatCurrency(event.cost)}
                          </span>
                        )}
                        {(event.assigned_to || event.performed_by) && (
                          <span>{event.assigned_to || event.performed_by}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EquipmentTimelinePage;